import { launcherModules } from 'components/navbar/top/moduleLauncherConfig';
import { getWorkspaceMode, isRouteVisibleForMode } from 'helpers/workspace';

export const REFERENCE_COMMANDS = [
  {
    id: 'posts',
    command: 'publicaciones',
    aliases: ['publicaciones', 'productos', 'items', 'catalogo', 'posts'],
    section: 'posts',
    labelKey: 'MAIN.POSTS',
    icon: 'file-invoice'
  },
  {
    id: 'stock',
    command: 'stock',
    aliases: ['stock', 'inventario', 'depositos', 'deposito', 'almacenes', 'almacen', 'warehouses', 'ubicaciones'],
    section: 'stock',
    labelKey: 'MAIN.STOCK',
    icon: 'box'
  },
  {
    id: 'sales',
    command: 'ventas',
    aliases: ['ventas', 'pedidos', 'sales', 'orders', 'venta'],
    section: 'sales',
    labelKey: 'MAIN.SALES',
    icon: 'shopping-cart'
  },
  {
    id: 'questions',
    command: 'preguntas',
    aliases: ['preguntas', 'pregunta', 'consultas', 'qa', 'questions'],
    section: 'questions',
    labelKey: 'MAIN.QUESTIONS',
    icon: 'question-circle'
  }
];

export const normalizeQuery = value => String(value || '').trim();

export const buildWildcardRegex = value => {
  const normalizedValue = normalizeQuery(value);
  if (!normalizedValue) return null;
  const parts = normalizedValue.split('*').map(part => part.trim()).filter(Boolean);
  if (!parts.length) return null;
  return new RegExp(parts.map(part => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('.*'), 'i');
};

export const matchesWildcard = (query, text) => {
  const normalizedText = String(text || '').trim();
  const regex = buildWildcardRegex(query);
  if (!regex || !normalizedText) return false;
  if (regex.test(normalizedText)) return true;
  const compactQuery = normalizeQuery(query).replace(/\*/g, '').toLowerCase();
  const compactText = normalizedText.toLowerCase();
  return compactQuery && compactText.includes(compactQuery);
};

export const filterVisibleModules = (visibleModules, query, t) => {
  if (!query) {
    return visibleModules.slice(0, 6);
  }
  return visibleModules.filter(module =>
    matchesWildcard(query, module.command) ||
    matchesWildcard(query, t(module.titleKey)) ||
    matchesWildcard(query, module.id) ||
    (module.aliases || []).some(keyword => matchesWildcard(query, keyword)) ||
    (module.keywords || []).some(keyword => matchesWildcard(query, keyword))
  );
};

export const parseScopedCommand = (value, visibleModules, t, availableCommands) => {
  const normalizedValue = normalizeQuery(value);
  const commandCatalog = availableCommands || visibleModules || [];

  if (!normalizedValue.startsWith('/')) {
    return {
      commandQuery: '',
      module: null,
      contentQuery: normalizedValue,
      isSlashCommand: false
    };
  }

  const withoutSlash = normalizedValue.slice(1).trim();
  if (!withoutSlash) {
    return {
      commandQuery: '',
      module: null,
      contentQuery: '',
      isSlashCommand: true
    };
  }

  const parts = withoutSlash.split(/\s+/);
  const command = String(parts.shift() || '').toLowerCase();
  const contentQuery = parts.join(' ').trim();
  const module = commandCatalog.find(item => {
    const searchableValues = [
      item.command,
      item.id,
      item.labelKey ? t(item.labelKey) : '',
      ...(item.aliases || [])
    ];
    return searchableValues.some(searchableValue =>
      String(searchableValue || '').toLowerCase() === command
    );
  }) || null;

  return {
    commandQuery: command,
    module,
    contentQuery,
    isSlashCommand: true
  };
};

export const buildVisibleLauncherModules = ({ hasPermission, hasAnyPermission, hasPlanModule, userData }) =>
  launcherModules.filter(module => {
    const mode = getWorkspaceMode(userData);
    if (!isRouteVisibleForMode(mode, module.modes)) {
      return false;
    }
    if (module.requiredPermission && !hasPermission(module.requiredPermission)) {
      return false;
    }
    if (module.requiredAnyPermissions && !hasAnyPermission(module.requiredAnyPermissions)) {
      return false;
    }
    if (module.id && typeof hasPlanModule === 'function' && !hasPlanModule(module.id)) {
      return false;
    }
    return true;
  });

export const mapSearchResultToReference = result => ({
  id: result.id,
  section: result.section,
  title: result.title,
  subtitle: result.subtitle || '',
  url: result.url,
  image_url: result.image_url || '',
  icon: result.icon || '',
  badge: result.badge || '',
  section_label: result.section_label || ''
});

export const buildSlashCommandSuggestions = (commands, t, commandQuery = '') => {
  const normalizedCommandQuery = String(commandQuery || '').trim().toLowerCase();

  return (commands || [])
    .filter(command => {
      if (!normalizedCommandQuery) {
        return true;
      }

      const searchableValues = [
        command.command,
        command.id,
        command.labelKey ? t(command.labelKey) : '',
        ...(command.aliases || [])
      ];

      return searchableValues.some(value =>
        String(value || '').toLowerCase().includes(normalizedCommandQuery)
      );
    })
    .map(command => ({
      id: command.id,
      command: command.command,
      section: command.section || command.id,
      title: `/${command.command}`,
      subtitle: command.labelKey ? t(command.labelKey) : command.command,
      url: '',
      icon: command.icon || 'terminal',
      label: 'Slash',
      kind: 'slash_command'
    }));
};
